import { Button } from '@/components/shadcn/button'
import { ChartContainer, ChartLegendContent, ChartTooltip } from '@/components/shadcn/charts'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { IconRefreshOutline } from '@icons/IconRefreshOutline'
import clsx from 'clsx'
import { Bar, BarChart, CartesianGrid, Cell, Customized, LabelList, XAxis, YAxis } from 'recharts'
import { ComponentActionsContainer } from '../shared/ComponentActionsContainer'
import { ComponentHeader } from '../shared/ComponentHeader'
import { ComponentTitle } from '../shared/ComponentTitle'
import { normalizeRechartsData } from '../shared/rechartsDataUtils'
import { getComponentHeights } from '../utils'
import { ChartWaterfallDataSchema, ChartWaterfallUISchema } from './types'
import {
  calculateAngledAxisBandSize,
  calculateCategoryYAxisWidth,
  calculateVerticalTickAxisBandSize,
  CATEGORY_LABEL_WRAP_WIDTH,
  computeWaterfallExtents,
  formatWaterfallValue,
  generateDataAxisTicks,
  measureChartTextWidth,
  transformWaterfallData,
  TransformedWaterfallRow,
  wrapCategoryLabelLines,
} from './waterfallChartUtils'

const CAN_REFRESH = true
const FIXED_SIZE_PER_CATEGORY = 60
const CHART_PADDING = 80
const BASE_BAR_KEY = '__waterfallBase'
const VISIBLE_BAR_KEY = '__waterfallBar'

interface WaterfallConnectorsProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  xAxisMap?: Record<string, any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  yAxisMap?: Record<string, any>
  rows: TransformedWaterfallRow[]
  categoryKey: string
  isVertical: boolean
}

function WaterfallConnectors({ xAxisMap, yAxisMap, rows, categoryKey, isVertical }: WaterfallConnectorsProps) {
  if (!xAxisMap || !yAxisMap || rows.length < 2) return null

  const xAxis = Object.values(xAxisMap)[0]
  const yAxis = Object.values(yAxisMap)[0]
  if (!xAxis || !yAxis) return null

  const categoryAxis = isVertical ? yAxis : xAxis
  const valueAxis = isVertical ? xAxis : yAxis
  const bandwidth = categoryAxis.bandSize || categoryAxis.bandwidth || 20
  const barCenterOffset = bandwidth / 2

  return (
    <g className="waterfall-connectors">
      {rows.slice(0, -1).map((row, index) => {
        const nextRow = rows[index + 1]
        const categoryPos = categoryAxis.scale(row[categoryKey])
        const nextCategoryPos = categoryAxis.scale(nextRow[categoryKey])
        if (categoryPos === undefined || nextCategoryPos === undefined) return null

        const connectorValue = row.runningEnd
        const valuePos = valueAxis.scale(connectorValue)
        const start = categoryPos + barCenterOffset
        const end = nextCategoryPos + barCenterOffset

        if (isVertical) {
          return (
            <line
              key={`connector-${index}`}
              x1={valuePos}
              y1={start}
              x2={valuePos}
              y2={end}
              stroke="hsl(var(--foreground))"
              strokeWidth={1}
              strokeOpacity={0.45}
            />
          )
        }

        return (
          <line
            key={`connector-${index}`}
            x1={start}
            y1={valuePos}
            x2={end}
            y2={valuePos}
            stroke="hsl(var(--foreground))"
            strokeWidth={1}
            strokeOpacity={0.45}
          />
        )
      })}
    </g>
  )
}

interface WaterfallBarLabelsProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  xAxisMap?: Record<string, any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  yAxisMap?: Record<string, any>
  rows: TransformedWaterfallRow[]
  categoryKey: string
  valueUnit?: string
}

/** Renders value labels outside bars for the horizontal-bar (vertical layout) orientation. */
function WaterfallBarLabels({ xAxisMap, yAxisMap, rows, categoryKey, valueUnit }: WaterfallBarLabelsProps) {
  if (!xAxisMap || !yAxisMap) return null

  const xAxis = Object.values(xAxisMap)[0]
  const yAxis = Object.values(yAxisMap)[0]
  if (!xAxis || !yAxis) return null

  const categoryAxis = yAxis
  const valueAxis = xAxis
  const bandwidth = categoryAxis.bandSize || categoryAxis.bandwidth || 20
  const barCenterOffset = bandwidth / 2

  return (
    <g className="waterfall-bar-labels">
      {rows.map((row, index) => {
        const categoryPos = categoryAxis.scale(row[categoryKey])
        if (categoryPos === undefined) return null

        const y = categoryPos + barCenterOffset
        const valueStart = row.stepType === 'total' ? 0 : row.base
        const valueEnd = row.stepType === 'total' ? row.barSize : row.base + row.barSize
        const xStart = valueAxis.scale(Math.min(valueStart, valueEnd))
        const xEnd = valueAxis.scale(Math.max(valueStart, valueEnd))
        if (xStart === undefined || xEnd === undefined) return null

        const isDecrease = row.stepType === 'decrease'
        const labelX = isDecrease ? Math.min(xStart, xEnd) - 8 : Math.max(xStart, xEnd) + 8
        const textAnchor = isDecrease ? 'end' : 'start'

        return (
          <text
            key={`bar-label-${index}`}
            x={labelX}
            y={y}
            textAnchor={textAnchor}
            dominantBaseline="central"
            fontSize={11}
            fill="hsl(var(--foreground))"
          >
            {formatWaterfallValue(row.displayValue, valueUnit)}
          </text>
        )
      })}
    </g>
  )
}

export function ChartWaterfallContent({
  uiSchema,
  dataSchema,
  tileFilters,
  reloadHandler,
}: {
  uiSchema: ChartWaterfallUISchema
  dataSchema: ChartWaterfallDataSchema
  tileFilters?: React.ReactNode
  reloadHandler?: () => void
}) {
  const { title, description, chartConfig, emphasizeHeader } = uiSchema
  const {
    categoryKey,
    valueKey,
    stepTypeKey,
    colors,
    orientation,
    valueAxis,
    showConnectors = true,
    showBarLabels = true,
    showGrid = true,
    hideAxis = false,
    fixedSize = false,
    lineClampCategoryLabels,
    xAxisOrientation,
    config,
  } = chartConfig
  const { data } = dataSchema
  const { data: normalizedData, mapDataKey } = normalizeRechartsData(data, [categoryKey, valueKey, stepTypeKey])
  const normalizedCategoryKey = mapDataKey(categoryKey)
  const normalizedValueKey = mapDataKey(valueKey)
  const normalizedStepTypeKey = mapDataKey(stepTypeKey)

  const hasHeader = !!title && !!tileFilters
  const { contentHeight } = getComponentHeights(emphasizeHeader, hasHeader)
  /** Matches ChartBar: `vertical` = categories on Y-axis (horizontal bars). */
  const isVertical = orientation === 'vertical'

  const chartRows = transformWaterfallData(
    normalizedData,
    normalizedCategoryKey,
    normalizedValueKey,
    normalizedStepTypeKey,
    colors
  )
  const chartData = chartRows.map((row) => ({
    ...row,
    [BASE_BAR_KEY]: row.base,
    [VISIBLE_BAR_KEY]: row.barSize,
  }))

  const { min: dataMin, max: dataMax } = computeWaterfallExtents(chartRows)
  const configuredDomain = valueAxis?.domain
  const domainMin =
    configuredDomain?.[0] === 'auto' || configuredDomain?.[0] === undefined ? dataMin : Number(configuredDomain[0])
  const domainMax =
    configuredDomain?.[1] === 'auto' || configuredDomain?.[1] === undefined ? dataMax : Number(configuredDomain[1])

  const dataAxisTicks = generateDataAxisTicks(domainMin, domainMax)
  const tickMin = dataAxisTicks[0] ?? domainMin
  const tickMax = dataAxisTicks[dataAxisTicks.length - 1] ?? domainMax

  const dataRowCount = chartData.length
  const fixedHeight = isVertical ? dataRowCount * FIXED_SIZE_PER_CATEGORY + CHART_PADDING : undefined
  const fixedWidth = !isVertical ? dataRowCount * FIXED_SIZE_PER_CATEGORY + CHART_PADDING : undefined

  const categoryLabels = chartRows.map((item) => String(item[normalizedCategoryKey] || ''))
  const categoryYAxisWidth = calculateCategoryYAxisWidth(categoryLabels, lineClampCategoryLabels)
  const categoryXAxisHeight = lineClampCategoryLabels
    ? calculateVerticalTickAxisBandSize(categoryLabels, lineClampCategoryLabels)
    : calculateAngledAxisBandSize(categoryLabels, 45)
  const valueUnit = valueAxis?.unit
  const valueTickLabels = dataAxisTicks.map((tick) => formatWaterfallValue(tick, valueUnit))
  const valueXAxisHeight = calculateAngledAxisBandSize(valueTickLabels, 45)

  const valueAxisTickWidth = Math.max(...valueTickLabels.map((label) => measureChartTextWidth(label)), 56) + 12

  const legendPayload = (['total', 'increase', 'decrease'] as const).map((key) => ({
    label: config[key]?.label ?? key,
    key,
    color: colors[key],
  }))

  const TICK_LINE_HEIGHT = 16

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderClampedYAxisTick = (props: any) => {
    const { x, y, payload } = props
    const lines = wrapCategoryLabelLines(String(payload.value), CATEGORY_LABEL_WRAP_WIDTH, lineClampCategoryLabels!)

    return (
      <text x={x} y={y} fontSize={12} fill="hsl(var(--muted-foreground))">
        <title>{String(payload.value)}</title>
        {lines.map((line, i) => (
          <tspan
            key={i}
            x={x}
            dy={i === 0 ? -(lines.length - 1) * (TICK_LINE_HEIGHT / 2) : TICK_LINE_HEIGHT}
            textAnchor="end"
            dominantBaseline="central"
          >
            {line}
          </tspan>
        ))}
      </text>
    )
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderAngledValueAxisTick = (props: any) => {
    const { x, y, payload } = props
    const text = formatWaterfallValue(Number(payload.value), valueUnit)

    return (
      <text
        x={x}
        y={y}
        fontSize={12}
        fill="hsl(var(--muted-foreground))"
        transform={`rotate(-45, ${x}, ${y})`}
        textAnchor="end"
        dy={8}
      >
        {text}
      </text>
    )
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderClampedXAxisTick = (props: any) => {
    const { x, y, payload } = props
    const lines = wrapCategoryLabelLines(String(payload.value), CATEGORY_LABEL_WRAP_WIDTH, lineClampCategoryLabels!)

    return (
      <text
        x={x}
        y={y}
        fontSize={12}
        fill="hsl(var(--muted-foreground))"
        transform={`rotate(-90, ${x}, ${y})`}
        textAnchor="end"
      >
        <title>{String(payload.value)}</title>
        {lines.map((line, i) => (
          <tspan key={i} x={x} dy={i === 0 ? 0 : TICK_LINE_HEIGHT} dominantBaseline="central">
            {line}
          </tspan>
        ))}
      </text>
    )
  }

  const verticalLayout = (
    <>
      <YAxis
        dataKey={normalizedCategoryKey}
        type="category"
        tickLine={false}
        tickMargin={10}
        axisLine={false}
        width={categoryYAxisWidth}
        interval={0}
        {...(lineClampCategoryLabels ? { tick: renderClampedYAxisTick } : {})}
      />
      <XAxis
        type="number"
        hide={hideAxis}
        domain={[tickMin, tickMax]}
        ticks={dataAxisTicks}
        orientation={xAxisOrientation}
        tick={renderAngledValueAxisTick}
        interval={0}
        height={valueXAxisHeight}
      />
    </>
  )

  const horizontalLayout = (
    <>
      <XAxis
        dataKey={normalizedCategoryKey}
        type="category"
        tickLine={false}
        tickMargin={10}
        axisLine={false}
        interval={0}
        height={categoryXAxisHeight}
        {...(lineClampCategoryLabels
          ? { tick: renderClampedXAxisTick }
          : {
              angle: -45,
              textAnchor: 'end' as const,
            })}
      />
      <YAxis
        hide={hideAxis}
        domain={[tickMin, tickMax]}
        ticks={dataAxisTicks}
        width={valueAxisTickWidth}
        tickMargin={8}
        tickFormatter={(value: number) => formatWaterfallValue(value, valueUnit)}
      />
    </>
  )

  return (
    <div className="flex h-full flex-col">
      {hasHeader && (
        <ComponentHeader emphasize={emphasizeHeader}>
          <ComponentTitle title={title} description={description} />
          <ComponentActionsContainer>
            {CAN_REFRESH && reloadHandler && (
              <TooltipButton tooltip="Refresh data">
                <Button size="icon" variant="ghost" className="size-8 rounded-full" onClick={reloadHandler}>
                  <IconRefreshOutline className="size-4" />
                </Button>
              </TooltipButton>
            )}
            {tileFilters}
          </ComponentActionsContainer>
        </ComponentHeader>
      )}
      <div
        className={clsx({
          'pt-5': hasHeader,
          'overflow-y-auto': fixedSize && isVertical,
          'overflow-x-auto': fixedSize && !isVertical,
        })}
        style={{ height: contentHeight }}
      >
        <ChartContainer
          className={clsx('mx-auto aspect-auto min-h-0 [&_.recharts-wrapper]:overflow-visible', {
            'h-full w-full': !fixedSize,
            'w-full': fixedSize && isVertical,
            'h-full': fixedSize && !isVertical,
          })}
          config={config}
          legend={<ChartLegendContent items={legendPayload} className="pt-1" />}
          style={{
            ...(fixedSize && isVertical && fixedHeight ? { height: fixedHeight, minHeight: fixedHeight } : {}),
            ...(fixedSize && !isVertical && fixedWidth ? { width: fixedWidth, minWidth: fixedWidth } : {}),
          }}
        >
          <BarChart accessibilityLayer data={chartData} layout={orientation} barGap={0}>
            {showGrid && <CartesianGrid vertical={isVertical} horizontal={!isVertical} />}
            {isVertical ? verticalLayout : horizontalLayout}
            <ChartTooltip
              cursor={false}
              content={({ active, payload, label }) => {
                if (!active) return null
                const item = payload?.find((entry) => entry.dataKey === VISIBLE_BAR_KEY)
                const row = item?.payload as TransformedWaterfallRow | undefined
                if (!row) return null

                return (
                  <div className="grid min-w-[8rem] gap-1.5 rounded-lg border border-border/50 bg-offwhite px-2.5 py-1.5 text-xs shadow-xl">
                    <div className="font-medium text-foreground">{label}</div>
                    <span className="font-mono font-medium tabular-nums text-foreground">
                      {formatWaterfallValue(row.displayValue, valueUnit)}
                    </span>
                  </div>
                )
              }}
            />
            <Bar
              dataKey={BASE_BAR_KEY}
              stackId="waterfall"
              fill="transparent"
              isAnimationActive={false}
              legendType="none"
            />
            <Bar dataKey={VISIBLE_BAR_KEY} stackId="waterfall" radius={isVertical ? [0, 4, 4, 0] : [4, 4, 0, 0]}>
              {chartData.map((row, index) => (
                <Cell key={`waterfall-cell-${index}`} fill={row.barColor} />
              ))}
              {showBarLabels && !isVertical && (
                <LabelList
                  dataKey="displayValue"
                  position="top"
                  offset={8}
                  formatter={(value: number) => formatWaterfallValue(Number(value), valueUnit)}
                  style={{
                    fill: 'hsl(var(--foreground))',
                    fontSize: 11,
                  }}
                />
              )}
            </Bar>
            {(showConnectors || (showBarLabels && isVertical)) && (
              <Customized
                component={(props: {
                  xAxisMap?: WaterfallConnectorsProps['xAxisMap']
                  yAxisMap?: WaterfallConnectorsProps['yAxisMap']
                }) => (
                  <>
                    {showConnectors && (
                      <WaterfallConnectors
                        xAxisMap={props.xAxisMap}
                        yAxisMap={props.yAxisMap}
                        rows={chartRows}
                        categoryKey={normalizedCategoryKey}
                        isVertical={isVertical}
                      />
                    )}
                    {showBarLabels && isVertical && (
                      <WaterfallBarLabels
                        xAxisMap={props.xAxisMap}
                        yAxisMap={props.yAxisMap}
                        rows={chartRows}
                        categoryKey={normalizedCategoryKey}
                        valueUnit={valueUnit}
                      />
                    )}
                  </>
                )}
              />
            )}
          </BarChart>
        </ChartContainer>
      </div>
    </div>
  )
}
